import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, formatApiError, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast, Toaster } from "sonner";
import {
    Sparkle, Plus, Trash, ArrowUp, ChatCircleText,
    Info, X, Lightning,
} from "@phosphor-icons/react";

/* --------------------------------------------------------------------- */
/*                            Ask Finance — v2                            */
/* Modern minimal chat: no boring cards. Full-bleed canvas, brand accent, */
/* Claude/Perplexity-style message flow, live tool trace as inline chips. */
/* --------------------------------------------------------------------- */

const CATEGORY_META = {
    meta:       { label: "General",     color: "#64748b" },
    ledgers:    { label: "Ledgers",     color: "#6366f1" },
    financials: { label: "Financials",  color: "#10b981" },
    txn:        { label: "Invoices",    color: "#f59e0b" },
    hr:         { label: "HR",          color: "#ec4899" },
    contract:   { label: "Contract",    color: "#a855f7" },
    assets:     { label: "Assets",      color: "#ef4444" },
    tax:        { label: "Compliance",   color: "#0891b2" },
    bank:       { label: "Bank",        color: "#14b8a6" },
    workflow:   { label: "Workflow",    color: "#f43f5e" },
};

export default function AskFinance() {
    const { user } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [streamText, setStreamText] = useState("");
    const [toolTrace, setToolTrace] = useState([]);
    const [catalogue, setCatalogue] = useState([]);
    const [showCatalogue, setShowCatalogue] = useState(false);
    const scrollRef = useRef(null);
    const inputRef = useRef(null);

    const refresh = useCallback(async () => {
        try {
            const [s, c] = await Promise.all([
                api.get("/agent/sessions"),
                api.get("/agent/catalogue"),
            ]);
            setSessions(s.data.sessions);
            setCatalogue(c.data.tools);
        } catch (e) { toast.error(formatApiError(e)); }
    }, []);
    useEffect(() => { refresh(); }, [refresh]);

    const loadSession = useCallback(async (sid) => {
        setActiveId(sid);
        setStreamText(""); setToolTrace([]);
        try {
            const { data } = await api.get(`/agent/sessions/${sid}`);
            setMessages(data.messages);
        } catch (e) { toast.error(formatApiError(e)); }
    }, []);

    const newSession = async () => {
        setActiveId(null);
        setMessages([]);
        setStreamText(""); setToolTrace([]);
        setTimeout(() => inputRef.current?.focus(), 50);
    };

    const deleteSession = async (sid) => {
        if (!confirm("Delete this chat?")) return;
        try {
            await api.delete(`/agent/sessions/${sid}`);
            if (sid === activeId) { setActiveId(null); setMessages([]); }
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, streamText, toolTrace]);

    const send = async () => {
        const text = input.trim();
        if (!text || streaming) return;
        let sid = activeId;
        if (!sid) {
            try {
                const { data } = await api.post("/agent/sessions",
                    { title: text.slice(0, 60) });
                sid = data.id;
                setActiveId(sid);
                refresh();
            } catch (e) { toast.error(formatApiError(e)); return; }
        }
        setInput("");
        setMessages((m) => [...m, { role: "user", content: text }]);
        setStreamText(""); setToolTrace([]); setStreaming(true);

        try {
            const res = await fetch(`${API}/agent/chat`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sid, message: text }),
            });
            if (!res.ok || !res.body) {
                const errText = await res.text().catch(() => "network error");
                throw new Error(errText || `HTTP ${res.status}`);
            }
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buf = "";
            let acc = "";
            let trace = [];
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const chunks = buf.split("\n\n");
                buf = chunks.pop();
                for (const chunk of chunks) {
                    const lines = chunk.split("\n");
                    const evtLine = lines.find((l) => l.startsWith("event: "));
                    const dataLine = lines.find((l) => l.startsWith("data: "));
                    if (!evtLine || !dataLine) continue;
                    const evt = evtLine.slice(7).trim();
                    let payload;
                    try { payload = JSON.parse(dataLine.slice(6)); } catch { continue; }
                    if (evt === "delta") {
                        acc += payload.text || "";
                        setStreamText(acc);
                    } else if (evt === "tool_start") {
                        trace = [...trace, { ...payload, status: "running" }];
                        setToolTrace(trace);
                    } else if (evt === "tool_result") {
                        const idx = trace.findIndex((x) => x.name === payload.name && x.status === "running");
                        if (idx >= 0) trace[idx] = { ...trace[idx], status: "done" };
                        setToolTrace([...trace]);
                    } else if (evt === "done") {
                        setMessages((m) => [...m, { role: "assistant", content: payload.final, tools: [...trace] }]);
                        setStreamText("");
                        setToolTrace([]);
                    } else if (evt === "error") {
                        toast.error(payload.message || "Agent error");
                    }
                }
            }
        } catch (e) {
            toast.error(e.message || "Chat failed");
        } finally {
            setStreaming(false);
            refresh();
        }
    };

    const grouped = useMemo(() => {
        const g = {};
        catalogue.forEach((t) => { (g[t.category] = g[t.category] || []).push(t); });
        return g;
    }, [catalogue]);

    return (
        <>
            <Toaster richColors position="top-right" />

            <div className="flex h-[calc(100vh-64px)] bg-white" data-testid="ask-page">
                {/* --------------------------- LEFT RAIL --------------------------- */}
                <aside className="w-64 border-r flex flex-col shrink-0"
                       style={{ borderColor: "var(--se-border)", background: "#fafaf9" }}>
                    <div className="p-4">
                        <button
                            onClick={newSession}
                            className="w-full flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-white transition-transform hover:scale-[1.02] active:scale-100"
                            style={{ background: "var(--se-brand-grad)" }}
                            data-testid="ask-new-session"
                        >
                            <Plus size={14} weight="bold" /> New chat
                        </button>
                    </div>
                    <div className="px-4 pb-2 text-[10px] font-semibold tracking-[0.12em] uppercase text-stone-400">
                        Recent
                    </div>
                    <nav className="flex-1 overflow-y-auto px-2 pb-4 space-y-0.5">
                        {sessions.length === 0 ? (
                            <div className="px-3 py-4 text-xs text-stone-400">Ask your first question below.</div>
                        ) : sessions.map((s) => (
                            <button
                                key={s.id}
                                onClick={() => loadSession(s.id)}
                                className={`group w-full text-left px-3 py-2 rounded-md flex items-start gap-2 transition ${
                                    activeId === s.id ? "bg-white shadow-sm" : "hover:bg-white/70"
                                }`}
                                style={activeId === s.id ? { boxShadow: "0 0 0 1px var(--se-border), 0 1px 2px rgba(0,0,0,0.04)" } : {}}
                                data-testid={`ask-session-${s.id}`}
                            >
                                <ChatCircleText size={14} className="mt-0.5 shrink-0"
                                    style={{ color: activeId === s.id ? "var(--se-brand)" : "#a8a29e" }} />
                                <span className="flex-1 min-w-0">
                                    <span className="block text-[13px] font-medium text-stone-800 truncate">{s.title}</span>
                                    <span className="block text-[10px] text-stone-400 mt-0.5">
                                        {relTime(s.updated_at)}
                                    </span>
                                </span>
                                <span
                                    onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}
                                    className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-rose-500 transition p-1 -m-1"
                                    data-testid={`ask-del-${s.id}`}
                                    role="button" tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); deleteSession(s.id); } }}
                                >
                                    <Trash size={12} />
                                </span>
                            </button>
                        ))}
                    </nav>
                    <div className="p-3 border-t text-[10px] text-stone-400 flex items-center gap-1"
                         style={{ borderColor: "var(--se-border)" }}>
                        <Lightning size={10} weight="fill" style={{ color: "var(--se-brand)" }} />
                        <span>Claude Sonnet 5 · {catalogue.length} live tools</span>
                    </div>
                </aside>

                {/* --------------------------- MAIN CANVAS --------------------------- */}
                <main className="flex-1 flex flex-col relative min-w-0">
                    {/* Floating hairline top bar */}
                    <div className="absolute inset-x-0 top-0 h-14 flex items-center justify-between px-6 z-10 bg-white/80 backdrop-blur-sm border-b"
                         style={{ borderColor: "var(--se-border)" }}>
                        <div className="flex items-center gap-2 text-sm text-stone-500">
                            <div className="w-2 h-2 rounded-full" style={{ background: "var(--se-brand)" }} />
                            <span className="font-medium text-stone-700">Ask Finance</span>
                            <span className="text-stone-300">·</span>
                            <span className="text-xs">grounded in your live books</span>
                        </div>
                        <button
                            onClick={() => setShowCatalogue(true)}
                            className="text-xs text-stone-500 hover:text-stone-800 flex items-center gap-1 rounded-md px-2 py-1 hover:bg-stone-100 transition"
                            data-testid="ask-show-catalogue"
                        >
                            <Info size={12} /> What can I ask?
                        </button>
                    </div>

                    <div ref={scrollRef}
                         className="flex-1 overflow-y-auto pt-14"
                         data-testid="ask-chat-pane">
                        {messages.length === 0 && !streamText && toolTrace.length === 0 ? (
                            <EmptyHero
                                userName={user?.name}
                                input={input}
                                setInput={setInput}
                                streaming={streaming}
                                send={send}
                                inputRef={inputRef}
                            />
                        ) : (
                            <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">
                                {messages.map((m, i) => (
                                    <Message key={i} role={m.role} content={m.content} tools={m.tools} />
                                ))}
                                {streaming ? (
                                    <Message role="assistant" content={streamText}
                                             tools={toolTrace} live />
                                ) : null}
                            </div>
                        )}
                    </div>

                    {/* Composer (bottom) — only shown once a chat has started */}
                    {(messages.length > 0 || streamText || toolTrace.length > 0) ? (
                        <div className="border-t px-6 py-4 bg-white" style={{ borderColor: "var(--se-border)" }}>
                            <div className="max-w-3xl mx-auto">
                                <Composer
                                    input={input} setInput={setInput}
                                    streaming={streaming} send={send} inputRef={inputRef}
                                    autoFocus={false}
                                />
                                <div className="text-[10px] text-stone-400 mt-2 text-center">
                                    Answers are grounded in your live data — every number is fetched, never guessed.
                                </div>
                            </div>
                        </div>
                    ) : null}
                </main>

                {/* --------------------------- CATALOGUE DRAWER --------------------------- */}
                {showCatalogue ? (
                    <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setShowCatalogue(false)}>
                        <div className="absolute inset-0 bg-stone-900/20 backdrop-blur-sm" />
                        <div className="relative w-[380px] h-full bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right"
                             onClick={(e) => e.stopPropagation()}>
                            <div className="sticky top-0 bg-white/95 backdrop-blur px-6 py-4 border-b flex items-center justify-between z-10"
                                 style={{ borderColor: "var(--se-border)" }}>
                                <div>
                                    <div className="text-sm font-semibold text-stone-800">What I can look up</div>
                                    <div className="text-xs text-stone-500 mt-0.5">{catalogue.length} live tools · auto-updates as new modules are added</div>
                                </div>
                                <button onClick={() => setShowCatalogue(false)}
                                        className="p-1.5 rounded-md hover:bg-stone-100"
                                        data-testid="ask-catalogue-close">
                                    <X size={16} />
                                </button>
                            </div>
                            <div className="p-6 space-y-6" data-testid="ask-catalogue">
                                {Object.entries(grouped).map(([cat, items]) => (
                                    <div key={cat}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <div className="w-1 h-4 rounded-full" style={{ background: CATEGORY_META[cat]?.color || "#94a3b8" }} />
                                            <div className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">
                                                {CATEGORY_META[cat]?.label || cat}
                                            </div>
                                            <div className="text-[10px] text-stone-400">{items.length}</div>
                                        </div>
                                        <div className="pl-3 space-y-1.5">
                                            {items.map((t) => (
                                                <div key={t.name} className="text-xs">
                                                    <code className="font-mono text-stone-700 text-[11px]">{t.name}</code>
                                                    <div className="text-stone-500 text-[11px] leading-snug mt-0.5">
                                                        {t.description}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </>
    );
}

/* --------------------------------------------------------------------- */
/*                              Message                                    */
/* --------------------------------------------------------------------- */
function Message({ role, content, tools, live }) {
    if (role === "user") {
        return (
            <div className="flex justify-end" data-testid="ask-msg-user">
                <div className="max-w-[80%] rounded-2xl rounded-tr-md px-4 py-2.5 text-[14px] leading-6 text-stone-900"
                     style={{ background: "var(--se-brand-grad-soft)" }}>
                    {content}
                </div>
            </div>
        );
    }
    return (
        <div className="flex gap-3" data-testid="ask-msg-ai">
            <div className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5 relative"
                 style={{ background: "var(--se-brand-grad)" }}>
                <Sparkle size={13} weight="fill" className="text-white" />
                {live ? (
                    <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-white animate-pulse" />
                ) : null}
            </div>
            <div className="flex-1 min-w-0">
                {tools && tools.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 mb-3" data-testid="ask-tool-trace">
                        {tools.map((t, i) => <ToolChip key={i} tool={t} />)}
                    </div>
                ) : null}
                {content ? (
                    <div className="prose prose-sm max-w-none text-stone-800
                                    prose-p:my-2 prose-p:leading-7
                                    prose-headings:mt-4 prose-headings:mb-2 prose-headings:font-semibold
                                    prose-h2:text-lg prose-h3:text-base
                                    prose-table:text-sm prose-table:my-3
                                    prose-th:font-semibold prose-th:text-stone-700 prose-th:px-2 prose-th:py-1.5 prose-th:border-b
                                    prose-td:px-2 prose-td:py-1.5
                                    prose-strong:text-stone-900 prose-strong:font-semibold
                                    prose-code:text-[12px] prose-code:bg-stone-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none
                                    prose-ul:my-2 prose-li:my-0.5">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
                        {live ? <span className="inline-block w-2 h-4 bg-stone-400 animate-pulse ml-0.5 align-middle rounded-sm" /> : null}
                    </div>
                ) : live ? (
                    <div className="text-sm text-stone-400 italic">Thinking…</div>
                ) : null}
            </div>
        </div>
    );
}

function ToolChip({ tool }) {
    const running = tool.status === "running";
    return (
        <div className="inline-flex items-center gap-1.5 rounded-full border pl-2 pr-2.5 py-1 text-[10px] font-mono transition"
             style={{
                 borderColor: running ? "#fecdd3" : "var(--se-border)",
                 background: running ? "#fff1f2" : "#fafaf9",
                 color: running ? "#9f1239" : "#57534e",
             }}>
            <span className={`w-1.5 h-1.5 rounded-full ${running ? "animate-pulse" : ""}`}
                  style={{ background: running ? "#f43f5e" : "#10b981" }} />
            <span>{tool.name}</span>
        </div>
    );
}

/* --------------------------------------------------------------------- */
/*                            Empty state                                  */
/* --------------------------------------------------------------------- */
/* --------------------------------------------------------------------- */
/*                     Composer (shared, minimal)                          */
/* --------------------------------------------------------------------- */
function Composer({ input, setInput, streaming, send, inputRef, autoFocus }) {
    useEffect(() => { if (autoFocus) inputRef.current?.focus(); }, [autoFocus, inputRef]);
    return (
        <div className="relative rounded-2xl border bg-white transition-colors"
             style={{ borderColor: "var(--se-border)" }}>
            <textarea
                ref={inputRef}
                rows="1"
                value={input}
                onChange={(e) => {
                    setInput(e.target.value);
                    e.target.style.height = "auto";
                    e.target.style.height = Math.min(e.target.scrollHeight, 200) + "px";
                }}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask anything about your books…"
                className="w-full resize-none bg-transparent px-5 pt-4 pb-12 text-[15px] leading-6 outline-none placeholder:text-stone-400 focus-visible:!shadow-none focus:!shadow-none"
                disabled={streaming}
                data-testid="ask-input"
            />
            <div className="absolute bottom-2.5 left-4 right-3 flex items-center justify-between">
                <div className="text-[10px] text-stone-400 flex items-center gap-1.5">
                    <kbd className="px-1.5 py-0.5 rounded bg-stone-50 border text-[9px] font-mono"
                         style={{ borderColor: "var(--se-border)" }}>↵</kbd>
                    <span>send</span>
                </div>
                <button
                    onClick={send}
                    disabled={streaming || !input.trim()}
                    className="rounded-full p-2 text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:scale-105 active:scale-95"
                    style={{ background: "var(--se-brand-grad)" }}
                    data-testid="ask-send"
                >
                    {streaming ? (
                        <span className="block w-3.5 h-3.5 rounded-sm bg-white/70 animate-pulse" />
                    ) : (
                        <ArrowUp size={14} weight="bold" />
                    )}
                </button>
            </div>
        </div>
    );
}

/* --------------------------------------------------------------------- */
/*                       Empty hero — Claude-style                        */
/* --------------------------------------------------------------------- */
const QUICK_CHIPS = [
    "Cash & bank today",
    "P&L this month",
    "Who owes me the most",
    "Payroll last month",
    "Top expenses this year",
    "Drafts older than a week",
];

function EmptyHero({ userName, input, setInput, streaming, send, inputRef }) {
    const first = (userName || "").split(" ")[0] || "there";
    const greeting = useMemo(() => {
        const opts = [
            `Hey ${first}.`,
            `What's on your mind, ${first}?`,
            `Ready when you are, ${first}.`,
            `Let's look at the books, ${first}.`,
        ];
        return opts[Math.floor(Math.random() * opts.length)];
    }, [first]);

    return (
        <div className="h-full flex flex-col items-center justify-center px-6" data-testid="ask-empty-hero">
            <div className="w-full max-w-2xl -mt-16">
                {/* Greeting */}
                <div className="flex items-center gap-3 mb-8 justify-center">
                    <div className="relative">
                        <Sparkle size={28} weight="fill" className="animate-[spin_10s_linear_infinite]"
                                 style={{ color: "var(--se-brand)" }} />
                    </div>
                    <h1 className="font-serif text-[34px] leading-none tracking-tight text-stone-900">
                        {greeting.slice(0, greeting.lastIndexOf(first))}
                        <span className="bg-clip-text text-transparent"
                              style={{ backgroundImage: "var(--se-brand-grad)" }}>
                            {first}
                        </span>
                        {greeting.slice(greeting.lastIndexOf(first) + first.length)}
                    </h1>
                </div>

                {/* Composer */}
                <Composer
                    input={input} setInput={setInput}
                    streaming={streaming} send={send} inputRef={inputRef}
                    autoFocus
                />

                {/* Chips */}
                <div className="mt-4 flex flex-wrap gap-2 justify-center">
                    {QUICK_CHIPS.map((c) => (
                        <button
                            key={c}
                            onClick={() => {
                                setInput(c);
                                setTimeout(() => inputRef.current?.focus(), 0);
                            }}
                            className="text-[12px] text-stone-600 rounded-full border px-3 py-1.5 bg-white hover:bg-stone-50 hover:border-stone-300 hover:text-stone-900 transition"
                            style={{ borderColor: "var(--se-border)" }}
                            data-testid={`ask-chip-${c.slice(0, 20)}`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* --------------------------------------------------------------------- */
/*                         Util: relative time                            */
/* --------------------------------------------------------------------- */
function relTime(iso) {
    if (!iso) return "";
    const ts = new Date(iso).getTime();
    if (Number.isNaN(ts)) return "";
    const diff = (Date.now() - ts) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
}
